"use client";

import * as React from "react";
import { useEffect, useState } from "react";
import { Search, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { EcommerceProductsProvider, useEcommerceProducts, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { ProductsSearch, useWPQueryForSearch, buildProductSearchUrl } from "@/components/shop/ProductsSearch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

// Recursive sub-item list for desktop dropdown navigation
function RecursiveDesktopMenu({
  items
}: {
  items: MenuItem[];
}) {
  return <ul className="min-w-[220px] divide-y divide-border border border-border bg-popover p-1 text-popover-foreground shadow-xs">
      {items.map((subItem, index) => <li key={subItem.id} data-index={index} className="py-1">
          <NavigationMenuLink asChild>
            <Link to={subItem.href || "#"} className="block px-3 py-2 text-xs font-semibold tracking-wider uppercase text-foreground hover:text-primary hover:bg-muted">
              {subItem.label}
            </Link>
          </NavigationMenuLink>
          {subItem.children && subItem.children.length > 0 && <div className="pl-3">
              <RecursiveDesktopMenu items={subItem.children} />
            </div>}
        </li>)}
    </ul>;
}

// Consumer for primary desktop navigation
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-4">
        <div className="h-4 w-16 animate-pulse bg-muted" />
        <div className="h-4 w-20 animate-pulse bg-muted" />
        <div className="h-4 w-24 animate-pulse bg-muted" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="flex items-center gap-1">
        {menuItems.map((item, index) => <NavigationMenuItem key={item.id} data-index={index}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="h-10 rounded-none bg-transparent px-3 text-xs font-semibold tracking-wider uppercase text-foreground transition-colors hover:text-primary hover:bg-muted focus:text-primary focus:bg-muted">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="rounded-none border-border bg-popover shadow-md">
                  <RecursiveDesktopMenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex h-10 items-center px-3 text-xs font-semibold tracking-wider uppercase text-foreground transition-colors hover:text-primary hover:bg-muted">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}

// Recursive list for mobile sheet navigation
function RecursiveMobileMenu({
  items,
  onNavigate
}: {
  items: MenuItem[];
  onNavigate: () => void;
}) {
  return <ul className="flex flex-col border-l border-border pl-3 space-y-1 mt-1">
      {items.map((item, index) => <li key={item.id} data-index={index}>
          <Link to={item.href || "#"} onClick={onNavigate} className="block py-1.5 text-xs font-medium tracking-wide uppercase text-muted-foreground hover:text-primary">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <RecursiveMobileMenu items={item.children} onNavigate={onNavigate} />}
        </li>)}
    </ul>;
}

// Mobile drawer navigation consumer
function MobileNavConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-4 py-4">
        <div className="h-6 w-3/4 animate-pulse bg-muted" />
        <div className="h-6 w-1/2 animate-pulse bg-muted" />
        <div className="h-6 w-2/3 animate-pulse bg-muted" />
      </div>;
  }
  return <nav className="flex flex-col space-y-3 py-4">
      {menuItems.map((item, index) => <div key={item.id} data-index={index} className="border-b border-border pb-3">
          <Link to={item.href || "#"} onClick={onClose} className="block text-sm font-semibold tracking-wider uppercase text-foreground hover:text-primary">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <RecursiveMobileMenu items={item.children} onNavigate={onClose} />}
        </div>)}
    </nav>;
}

// Consumer for product search results inside Popover
function SearchResultsConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="p-4 text-xs font-mono uppercase tracking-wider text-muted-foreground">
        Scanning catalog inventory...
      </div>;
  }
  if (error) {
    return <div className="p-4 text-xs font-mono uppercase tracking-wider text-destructive">
        Error loading equipment.
      </div>;
  }
  if (!products.length) {
    return <div className="p-4 text-xs font-mono uppercase tracking-wider text-muted-foreground">
        No industrial units or parts matched your search.
      </div>;
  }
  return <div className="grid grid-cols-1 divide-y divide-border border border-border bg-card">
      {products.slice(0, 5).map((product, index) => <Link key={product.id} data-index={index} to={product.link || `/ShopPage`} className="flex items-center gap-3 p-3 transition-colors hover:bg-muted">
          {product.featured_image_url ? <div className="size-12 shrink-0 border border-border bg-muted">
              <img src={product.featured_image_url} alt={product.title} className="size-full object-cover" loading="lazy" />
            </div> : <div className="size-12 shrink-0 border border-border bg-muted flex items-center justify-center text-[10px] font-mono text-muted-foreground">
              SPEC
            </div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold uppercase tracking-wider text-foreground">
              {product.title}
            </p>
            <div className="mt-0.5 text-xs font-mono text-primary">
              <ProductPrice product={product} />
            </div>
          </div>
        </Link>)}
    </div>;
}

// Popover wrapper for catalog search
function EquipmentSearchPopover() {
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  return <EcommerceProductsProvider wp_query={wp_query}>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="size-9 rounded-none text-foreground hover:bg-muted" aria-label="Search equipment catalog">
            <Search className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[calc(100vw-2rem)] sm:w-[420px] rounded-none border border-border bg-background p-4 shadow-xl">
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                Equipment & Parts Search
              </span>
            </div>
            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search by model, SKU, or part..." className="w-full" />
            <div className="max-h-72 overflow-y-auto">
              {!hasSearchQuery ? <p className="py-3 text-xs font-mono text-muted-foreground">
                  Enter machine designation, motor spec, or replacement component.
                </p> : <div className="space-y-2">
                  <SearchResultsConsumer />
                  <Link to={buildProductSearchUrl(searchValue)} className="block pt-2 text-center text-xs font-mono uppercase tracking-wider text-primary hover:underline">
                    View all matching equipment
                  </Link>
                </div>}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </EcommerceProductsProvider>;
}
export default function Header() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Smooth scroll listener for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerOffset = 80;
            const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
            window.scrollTo({
              top,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" data-nav="light" className="sticky top-0 z-50 w-full border-b border-border bg-background">
      <header className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Left: Commercial Brand Logo */}
        <div className="flex shrink-0 items-center">
          <Link to="/" className="flex items-center">
            <WvcLogo className="h-12 w-auto" />
          </Link>
        </div>

        {/* Center: Desktop Navigation */}
        <nav className="hidden lg:flex lg:items-center lg:justify-center" aria-label="Main Navigation">
          <MenuProvider menu_id="22">
            <DesktopNavConsumer />
          </MenuProvider>
        </nav>

        {/* Right: Technical Search & Industrial Contact CTA */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Equipment Search Popover */}
          <EquipmentSearchPopover />

          {/* Rust Industrial CTA */}
          <Button asChild variant="default" className="h-10 rounded-none bg-primary px-4 sm:px-6 text-xs font-semibold tracking-widest uppercase text-primary-foreground transition-colors hover:bg-primary/90">
            <Link to="/RequestQuotePage">Enquire Now</Link>
          </Button>

          {/* Mobile Drawer Navigation */}
          <div className="lg:hidden">
            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="size-10 rounded-none border-border bg-background text-foreground hover:bg-muted" aria-label="Open equipment menu">
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-sm rounded-none border-l border-border bg-background p-6">
                <SheetHeader className="border-b border-border pb-4 text-left">
                  <SheetTitle className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                    Equipment Navigation
                  </SheetTitle>
                </SheetHeader>

                <div className="mt-4 flex flex-col justify-between overflow-y-auto">
                  <MenuProvider menu_id="22">
                    <MobileNavConsumer onClose={() => setIsMobileOpen(false)} />
                  </MenuProvider>

                  <div className="mt-8 border-t border-border pt-6 space-y-4">
                    <div className="space-y-1">
                      <p className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                        Technical Inquiries
                      </p>
                      <a href="mailto:sales@mechanicalbullshop.com" className="block text-xs font-mono text-muted-foreground hover:text-foreground">
                        sales@mechanicalbullshop.com
                      </a>
                    </div>

                    <Button asChild className="w-full h-11 rounded-none bg-primary text-xs font-semibold tracking-widest uppercase text-primary-foreground hover:bg-primary/90">
                      <Link to="/RequestQuotePage" onClick={() => setIsMobileOpen(false)}>
                        Enquire Now
                      </Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>
    </section>;
}